import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { WSProvider } from "@/context/WSContext";
import { AlertsProvider } from "@/context/AlertsContext";

import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import SuperAdminRoute from "@/components/SuperAdminRoute";
import SuperAdminLayout from "@/layouts/SuperAdminLayout";
import PortalRoute from "@/components/PortalRoute";
import PortalLayout from "@/layouts/PortalLayout";
import PortalOverview from "@/pages/portal/PortalOverview";
import PortalLicenses from "@/pages/portal/PortalLicenses";
import PortalDevices from "@/pages/portal/PortalDevices";
import PortalDownloads from "@/pages/portal/PortalDownloads";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalTeam from "@/pages/portal/PortalTeam";
import PortalSupport from "@/pages/portal/PortalSupport";
import PortalSettings from "@/pages/portal/PortalSettings";
import SuperAdminOverview from "@/pages/superadmin/SuperAdminOverview";
import SuperAdminTenants from "@/pages/superadmin/SuperAdminTenants";
import SuperAdminTenantDetail from "@/pages/superadmin/SuperAdminTenantDetail";
import SuperAdminAlerts from "@/pages/superadmin/SuperAdminAlerts";
import SuperAdminAudit from "@/pages/superadmin/SuperAdminAudit";
import SuperAdminAnalytics from "@/pages/superadmin/SuperAdminAnalytics";
import SuperAdminBilling from "@/pages/superadmin/SuperAdminBilling";
import SuperAdminReleases from "@/pages/superadmin/SuperAdminReleases";
import SuperAdminDesktopReleases from "@/pages/superadmin/SuperAdminDesktopReleases";
import SuperAdminPreviewSoak from "@/pages/superadmin/SuperAdminPreviewSoak";
import AppMappings from "@/pages/AppMappings";
import DesktopSettings from "@/pages/DesktopSettings";
import SuperAdminSecurity from "@/pages/superadmin/SuperAdminSecurity";
import SuperAdminSettings from "@/pages/superadmin/SuperAdminSettings";
import SuperAdminLogin from "@/pages/superadmin/SuperAdminLogin";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Reports from "@/pages/Reports";
import Analytics from "@/pages/Analytics";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import Migrate from "@/pages/Migrate";
import MigrateStorage from "@/pages/MigrateStorage";
// MigratePC is intentionally NOT imported — the route was removed from the
// sidebar; legacy deep-links to /migrate/pc redirect to /migrations.
// DeployAgent likewise lost its standalone page; /deploy-agent now redirects
// to /access-tokens?tab=deploy-agent which renders the same panel — now
// surfaced directly in the sidebar as "Download Agent" (see Layout.jsx).
import MigrateServer from "@/pages/MigrateServer";
import Migrations from "@/pages/Migrations";
import AuditLog from "@/pages/AuditLog";
import Schedules from "@/pages/Schedules";
import Docs from "@/pages/Docs";
import Capabilities from "@/pages/Capabilities";
import Documentation from "@/pages/Documentation";
import About from "@/pages/About";
import UseCases from "@/pages/UseCases";
import HowItWorks from "@/pages/HowItWorks";
import Support from "@/pages/Support";
import SharePublic from "@/pages/SharePublic";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import Agents from "@/pages/Agents";
import Retention from "@/pages/Retention";
import ApiTokens from "@/pages/ApiTokens";
import SsoSettings from "@/pages/SsoSettings";
import SecuritySettings from "@/pages/SecuritySettings";
// (DeployAgent import removed — see note above on /migrate/pc & /deploy-agent.)
import AcceptInvite from "@/pages/AcceptInvite";
import StorageMigrationsNAS from "@/pages/StorageMigrationsNAS";
import Landing from "@/pages/Landing";
import { isDesktop } from "@/lib/desktopShell";
import { isPortalOnly } from "@/lib/portalOnly";
import { isSuperadminOnly } from "@/lib/superadminOnly";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCanceled from "@/pages/BillingCanceled";

/**
 * HomeRouter — controls what `/` renders.
 *
 * Unauthenticated visitors see the public marketing Landing page (the
 * face of moovbytes.com). We handle this here — outside the
 * ProtectedRoute tree — because ProtectedRoute unconditionally
 * redirects anonymous users to `/login`, which would hide the
 * marketing surface entirely.
 *
 * Desktop is the one exception: an already-installed app has no reason
 * to show "Create an account" / "Book demo" marketing copy, so an
 * unauthenticated desktop launch goes straight to /login instead.
 *
 * Authenticated: Desktop (the actual migration/transfer product) goes
 * to the migration dashboard at /dashboard, same as before this route
 * existed — just named rather than inlined. A browser session instead
 * lands on the Client Portal (/portal), the commercial account-
 * management surface — customers no longer land in the migration
 * dashboard straight out of login. isDesktop() is the same signal
 * Layout.jsx already uses to gate desktop-only nav items, so this adds
 * no new runtime concept, just a second branch on an existing one.
 *
 * A build running as the standalone Portal service (REACT_APP_PORTAL_ONLY,
 * see lib/portalOnly.js) never expects Desktop traffic and has no
 * marketing site of its own — unauthenticated visitors go straight to
 * /login rather than the SaaS Landing page.
 *
 * A build running as the standalone SuperAdmin service
 * (REACT_APP_SUPERADMIN_ONLY, see lib/superadminOnly.js) sends
 * unauthenticated visitors to /superadmin/login. An authenticated
 * non-superadmin can't be bounced to /superadmin (SuperAdminRoute would
 * just bounce them straight back to "/", looping) — they get a plain
 * access-restricted message instead, rendered here rather than
 * redirected anywhere.
 */
function HomeRouter() {
    const { user } = useAuth();
    if (user === undefined) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#03122b]">
                <div className="font-mono text-xs uppercase tracking-widest text-white/50">
                    Loading…
                </div>
            </div>
        );
    }
    if (isSuperadminOnly()) {
        if (user === null) return <Navigate to="/superadmin/login" replace />;
        if (user.role !== "superadmin") {
            return (
                <div className="flex min-h-screen items-center justify-center bg-[#050b1c] px-6 text-center">
                    <div>
                        <div className="font-display text-3xl font-bold text-white">Access restricted</div>
                        <div className="mt-2 text-sm text-white/50">
                            This console is for MoovBytes platform administrators only.
                        </div>
                    </div>
                </div>
            );
        }
        return <Navigate to="/superadmin" replace />;
    }
    if (user === null) return (isDesktop() || isPortalOnly()) ? <Navigate to="/login" replace /> : <Landing />;
    if (isPortalOnly()) return <Navigate to="/portal" replace />;
    if (isDesktop()) return <Navigate to="/dashboard" replace />;
    return <Navigate to="/portal" replace />;
}

/** Route element for a tree that must never render on a portal-only
 * deployment (the migration dashboard, SuperAdmin) — redirects into the
 * Portal instead of the real page. A no-op wrapper everywhere else. */
function NotOnPortalOnly({ children }) {
    if (isPortalOnly()) return <Navigate to="/portal" replace />;
    return children;
}

/** Same idea as NotOnPortalOnly, for the standalone SuperAdmin service —
 * the migration dashboard and Client Portal trees redirect into
 * /superadmin instead of rendering. */
function NotOnSuperadminOnly({ children }) {
    if (isSuperadminOnly()) return <Navigate to="/superadmin" replace />;
    return children;
}

function AppRoutes() {
    const { user } = useAuth();
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/onboard/billing-success" element={<BillingSuccess />} />
            <Route path="/onboard/billing-canceled" element={<BillingCanceled />} />
            <Route path="/" element={<HomeRouter />} />
            <Route path="/capabilities" element={<Capabilities />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/about" element={<About />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/support" element={<Support />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/share/:token" element={<SharePublic />} />
            <Route path="/superadmin/login" element={isPortalOnly() ? <Navigate to="/login" replace /> : <SuperAdminLogin />} />
            <Route
                path="/superadmin/*"
                element={
                    <NotOnPortalOnly>
                        <SuperAdminRoute>
                            <SuperAdminLayout />
                        </SuperAdminRoute>
                    </NotOnPortalOnly>
                }
            >
                <Route index element={<SuperAdminOverview />} />
                <Route path="tenants" element={<SuperAdminTenants />} />
                <Route path="tenants/:id" element={<SuperAdminTenantDetail />} />
                <Route path="alerts" element={<SuperAdminAlerts />} />
                <Route path="audit" element={<SuperAdminAudit />} />
                <Route path="analytics" element={<SuperAdminAnalytics />} />
                <Route path="billing" element={<SuperAdminBilling />} />
                <Route path="releases" element={<SuperAdminReleases />} />
                <Route path="desktop-releases" element={<SuperAdminDesktopReleases />} />
                <Route path="preview-soak" element={<SuperAdminPreviewSoak />} />
                <Route path="security" element={<SuperAdminSecurity />} />
                <Route path="settings" element={<SuperAdminSettings />} />
            </Route>
            {/* Client Portal — the commercial account-management surface
                (licenses, devices, downloads, billing, team, support,
                settings). Architecturally separate from the migration
                dashboard below: no migration jobs, transfer telemetry, or
                NAS/S3/Azure/GCS configuration ever appear here. */}
            <Route
                path="/portal/*"
                element={
                    <NotOnSuperadminOnly>
                        <PortalRoute>
                            <PortalLayout />
                        </PortalRoute>
                    </NotOnSuperadminOnly>
                }
            >
                <Route index element={<PortalOverview />} />
                <Route path="licenses" element={<PortalLicenses />} />
                <Route path="devices" element={<PortalDevices />} />
                <Route path="downloads" element={<PortalDownloads />} />
                <Route path="billing" element={<PortalBilling />} />
                <Route path="team" element={<PortalTeam />} />
                <Route path="support" element={<PortalSupport />} />
                <Route path="settings" element={<PortalSettings />} />
            </Route>
            <Route
                path="/*"
                element={
                    <NotOnPortalOnly>
                    <NotOnSuperadminOnly>
                    <ProtectedRoute>
                        <Layout>
                            <Routes>
                                <Route index element={<Dashboard />} />
                                <Route path="dashboard" element={<Dashboard />} />
                                <Route path="migrate" element={<Migrate />} />
                                <Route path="migrate/storage" element={<StorageMigrationsNAS />} />
                                <Route path="migrate/storage/legacy" element={<MigrateStorage />} />
                                <Route path="migrate/storage/:jobId" element={<StorageMigrationsNAS />} />
                                <Route path="migrate/pc" element={<Navigate to="/migrations" replace />} />
                                <Route path="migrate/server" element={<MigrateServer />} />
                                <Route path="migrate/server/:jobId" element={<MigrateServer />} />
                                <Route path="migrations" element={<Migrations />} />
                                <Route path="schedules" element={<Schedules />} />
                                <Route path="docs" element={<Docs />} />
                                <Route path="jobs" element={<Jobs />} />
                                <Route path="jobs/:id" element={<JobDetail />} />
                                <Route path="reports" element={<Reports />} />
                                <Route path="analytics" element={<Analytics />} />
                                <Route path="alerts" element={<Alerts />} />
                                <Route path="audit" element={<AuditLog />} />
                                <Route path="agents" element={<Agents />} />
                                <Route path="notifications" element={<Notifications />} />
                                <Route path="users" element={<Users />} />
                                <Route path="retention" element={<Retention />} />
                                <Route path="sso-settings" element={<SsoSettings />} />
                                <Route path="security" element={<SecuritySettings />} />
                                <Route path="access-tokens" element={<ApiTokens />} />
                                <Route path="app-mappings" element={<AppMappings />} />
                                <Route path="desktop-settings" element={isDesktop() ? <DesktopSettings /> : <Navigate to="/" replace />} />
                                {/* Deploy Agent now lives as a tab on /access-tokens. The standalone
                                    page route still resolves to that tab so any bookmarks /
                                    in-app deep-links keep working. */}
                                <Route path="deploy-agent" element={<Navigate to="/access-tokens?tab=deploy-agent" replace />} />
                                {/* Backwards-compat: the old /api-tokens path collided with the
                                    K8s `/api*` ingress prefix on production (requests were sent
                                    to the FastAPI backend and 404'd). Permanent client-side
                                    redirect so bookmarks resolve correctly. */}
                                <Route path="api-tokens" element={<Navigate to="/access-tokens" replace />} />
                                {/* Settings now only hosts Retention + API Tokens tabs for legacy deep-links. */}
                                <Route path="settings" element={<Settings />} />
                            </Routes>
                        </Layout>
                    </ProtectedRoute>
                    </NotOnSuperadminOnly>
                    </NotOnPortalOnly>
                }
            />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <BrowserRouter>
                <AuthProvider>
                    <InnerApp />
                </AuthProvider>
            </BrowserRouter>
        </ThemeProvider>
    );
}

function InnerApp() {
    return (
        <WSProvider>
            <AlertsProvider>
                <Toaster position="top-right" richColors closeButton />
                <AppRoutes />
            </AlertsProvider>
        </WSProvider>
    );
}

export default App;
